import {
  ChevronDown,
  Mail,
  Phone,
  RefreshCw,
  Star,
  User,
  UserPlus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  formatCurrency,
  formatDateCard,
  MetadataRequestBadges,
  OrderCardUIProps,
  PaymentIcon,
  ProgressBar,
  SHIPPING_TYPE_OPTIONS,
} from './order-shared';

export function OrderCardList({
  order,
  orderNum,
  storeName,
  customerName,
  customerEmail,
  customerPhone,
  billingLine,
  shippingLine,
  flagEmoji,
  salespersonName,
  paymentMethodStyle,
  paymentStatusStyle,
  flagInfo,
  progress,
  shippingType,
  isApproved,
  isStaff,
  onCardClick,
  onShippingTypeSelect,
  onNoteOpenChange,
  onPaymentOpenChange,
  onSalespersonOpenChange,
}: OrderCardUIProps) {
  return (
    <div
      className="flex min-h-44 cursor-pointer rounded-2xl border border-smoke bg-neutral-white shadow-sm transition-colors hover:border-primary-s500"
      onClick={onCardClick}
    >
      {/* ── S1: Order + Store ── */}
      <div className="flex w-36 shrink-0 flex-col items-center justify-center gap-1.5 border-r border-smoke px-3 py-3">
        <div className="flex items-center gap-1">
          <Star size={12} className="fill-secondary-s500 text-secondary-s500 shrink-0" />
          <span className="text-sm font-bold text-black">{orderNum}</span>
        </div>
        <span className="block text-xs font-medium leading-tight text-lightGray">{order.orderNumber}</span>
        <p className="text-[13px] font-bold text-black leading-tight">{storeName}</p>
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full border ${paymentStatusStyle.wrapper}`}
        >
          <span className={`text-sm font-bold ${paymentStatusStyle.icon}`}>$</span>
        </div>
        <p className="text-xs font-medium text-lightGray">{formatDateCard(order.createdAt)}</p>
      </div>

      {/* ── S2: Customer info + IP address ── */}
      <div className="flex w-62 shrink-0 flex-col justify-center gap-1.5 border-r border-smoke px-3 py-3">
        <div className="w-full space-y-2 rounded-xl border border-smoke px-3 py-2.5">
          <div className="flex items-center gap-1.5">
            <User size={12} className="shrink-0 text-silver" />
            <span className="truncate text-[13px] text-black">{customerName}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Mail size={12} className="shrink-0 text-silver" />
            <span className="break-all text-xs font-medium text-lightGray">{customerEmail}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Phone size={12} className="shrink-0 text-silver" />
            <span className="text-xs font-medium text-lightGray">{customerPhone}</span>
          </div>
        </div>
        <div className="w-full rounded-xl border border-smoke px-3 py-2.5">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-lightGray">
            IP Address
          </p>
          <span className="text-xs text-black">{order.ipAddress || 'N/A'}</span>
        </div>
      </div>

      {/* ── S3: Billing + Shipping ── */}
      <div className="flex w-68 shrink-0 flex-col justify-center gap-1.5 border-r border-smoke px-3 py-3">
        <div className="rounded-xl border border-smoke px-2.5 py-2">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-lightGray">
            Billing Address
          </p>
          <p className="truncate text-xs text-black leading-snug">{billingLine}</p>
        </div>
        <div className="rounded-xl border border-smoke px-2.5 py-2">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-lightGray">
            Shipping Address
          </p>
          <div className="flex items-start gap-1.5">
            {flagEmoji && <span className="shrink-0 text-[15px] leading-none">{flagEmoji}</span>}
            <p className="text-xs text-black leading-snug">{shippingLine}</p>
          </div>
        </div>
      </div>

      {/* ── S4: Shipping Type ── */}
      <div
        className="flex min-w-68 flex-1 flex-col justify-center gap-2 border-r border-smoke px-3 py-3"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[11px] font-semibold uppercase tracking-wide text-lightGray">
          Shipping Type
        </p>
        <div>
          <p className="mb-1 text-xs font-medium text-lightGray">Order Status</p>
          <ProgressBar value={progress.value} color={progress.color} />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-xl border border-smoke bg-neutral-white px-2.5 py-1.5 text-xs text-gray hover:bg-smoke1"
            >
              <span className="truncate">{shippingType}</span>
              <ChevronDown size={12} className="ml-1 shrink-0 text-silver" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 max-h-64 overflow-y-auto">
            {SHIPPING_TYPE_OPTIONS.map((value) => (
              <DropdownMenuItem
                key={value}
                onClick={() => onShippingTypeSelect(value)}
                className={shippingType === value ? 'bg-primary-s50 text-primary-s500' : ''}
              >
                {value}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <p className={`text-xs font-medium ${progress.textColor}`}>{progress.label}</p>
        {order.status === 'Cancellation Requested' && (
          <span className="rounded-full bg-danger px-2.5 py-0.5 text-[11px] font-semibold text-neutral-white w-fit">
            Cancellation Request
          </span>
        )}
        <MetadataRequestBadges metadata={order.metadata} size="sm" />
      </div>

      {/* ── S5: Marketplace + Amount ── */}
      <div className="flex w-36 shrink-0 flex-col justify-center gap-1.5 border-r border-smoke px-3 py-3">
        <span className="self-start rounded-full bg-danger-light px-3 py-0.5 text-[11px] font-semibold uppercase text-danger">
          {order.marketplace || 'Caterpiller'}
        </span>
        <span className="text-xl font-bold text-black">
          {formatCurrency(order.totalAmount)} USD
        </span>
        <p className="text-xs font-medium text-primary-s500">Revenue {formatCurrency(order.totalAmount)} USD</p>
      </div>

      {/* ── S6: Payment method + actions ── */}
      <div
        className="flex w-40 shrink-0 flex-col justify-center gap-2 border-r border-smoke px-3 py-3"
        onClick={(e) => e.stopPropagation()}
      >
        {paymentMethodStyle && (
          <div className="flex items-center gap-2">
            <PaymentIcon
              iconName={paymentMethodStyle.iconName}
              imageName={paymentMethodStyle.imageName}
              lucideIcon={paymentMethodStyle.lucideIcon}
              iconColor={paymentMethodStyle.iconColor}
              size={28}
              wrapperClassName={`flex h-7 w-7 items-center justify-center rounded-full ${paymentMethodStyle.wrapper}`}
            />
            <span className="text-[13px] font-semibold text-black">{paymentMethodStyle.label}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-lightGray">
            {Array.isArray(order.metadata?.salespersonFiles) && order.metadata.salespersonFiles.length > 0 ? 'Has Files' : 'No Files'}
          </span>
          {isApproved && (
            <span className="rounded-full bg-primary-s50 px-2.5 py-0.5 text-xs font-semibold text-primary-s500">
              Approval
            </span>
          )}
        </div>
        {order.metadata?.notes && order.metadata.notes.length > 0 && (
          <div className="flex flex-col gap-1 rounded-lg border border-smoke bg-smoke1 px-2.5 py-2">
            {order.metadata.notes.map((note) => (
              <p key={note.id} className="text-xs font-medium text-lightGray leading-snug line-clamp-2">
                {note.text}
              </p>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => onPaymentOpenChange(true)}
          className="self-start text-xs text-primary-s500 hover:text-blue-700"
        >
          More...
        </button>
      </div>

      {/* ── S7: Flag + Salesperson + actions ── */}
      <div
        className="flex w-32 shrink-0 flex-col items-center justify-center gap-2 px-3 py-3"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="rounded-lg border border-dark5 px-3 py-1.5 text-xs font-normal text-lightGray hover:bg-smoke1 transition-colors"
          onClick={() => onNoteOpenChange(true)}
        >
          + Add Note
        </button>
        {flagInfo && (
          <p className="w-full text-xs text-center font-normal">
            Flag:{' '}
            <span className={`font-semibold ${flagInfo.color}`}>{flagInfo.label}</span>
          </p>
        )}
        <span className="w-full text-center text-[13px] font-bold text-black leading-tight">
          {salespersonName ?? (
            <span className="text-xs font-medium text-lightGray">Unassigned</span>
          )}
        </span>
        {!isStaff && (
          <div className="flex items-center gap-2">
            {salespersonName ? (
              <button
                type="button"
                title="Reassign Salesperson"
                onClick={() => onSalespersonOpenChange(true)}
                className="text-lightGray hover:text-black"
              >
                <RefreshCw size={14} />
              </button>
            ) : (
              <button
                type="button"
                title="Assign Salesperson"
                onClick={() => onSalespersonOpenChange(true)}
                className="text-lightGray hover:text-black"
              >
                <UserPlus size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
